import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { fetchShipments } from '../../services/shipment';
import CustomPagination from '../../components/common/CustomPagination';

const statusColor = (status) => {
  if (status === 'delivered') return 'success';
  if (status === 'arrived') return 'info';
  if (status === 'in_transit') return 'warning';
  return 'default';
};

const resolveRowFromValueGetterArgs = (firstArg, secondArg) => {
  if (secondArg && typeof secondArg === 'object') return secondArg;
  if (firstArg && typeof firstArg === 'object' && firstArg.row) return firstArg.row;
  return undefined;
};

export default function ShipmentList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [shipmentNumberFilter, setShipmentNumberFilter] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);
  const [total, setTotal] = useState(0);
  const totalPages = Math.ceil(total / pageSize) || 1;
  const rowsWithSerial = rows.map((row, idx) => ({
    ...row,
    sn: page * pageSize + idx + 1,
  }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchShipments({
        page: page + 1,
        page_size: pageSize,
        search,
        shipment_number: shipmentNumberFilter,
      });
      setRows(data.results || []);
      setTotal(data.count || 0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, shipmentNumberFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = [
    { field: 'sn', headerName: 'S/N', width: 80, sortable: false, filterable: false },
    { field: 'shipment_number', headerName: 'Shipment Number', width: 180 },
    { field: 'container_number', headerName: 'Container Number', width: 220 },
    { field: 'carrier', headerName: 'Carrier', width: 120 },
    { field: 'provider', headerName: 'Provider', width: 120 },
    {
      field: 'shipment_type',
      headerName: 'Shipment Type',
      width: 130,
      valueGetter: (firstArg, secondArg) => {
        const row = resolveRowFromValueGetterArgs(firstArg, secondArg);
        return row?.shipment_type || '-';
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => <Chip size="small" label={params.value} color={statusColor(params.value)} />
    },
    {
      field: 'is_sent',
      headerName: 'Sent',
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.row.is_sent ? 'Sent' : 'Not Sent'}
          color={params.row.is_sent ? 'success' : 'warning'}
        />
      )
    },
    { field: 'item_count', headerName: 'Item Count', width: 110 },
    {
      field: 'created_at',
      headerName: 'Created At',
      width: 190,
      valueGetter: (firstArg, secondArg) => {
        const row = resolveRowFromValueGetterArgs(firstArg, secondArg);
        return row?.created_at ? new Date(row.created_at).toLocaleString() : '-';
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 180,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" onClick={() => navigate(`/admin/shipments/${params.row.id}`)}>View</Button>
          <Button size="small" onClick={() => navigate(`/admin/shipments/${params.row.id}/edit`)}>Edit</Button>
        </Stack>
      )
    }
  ];

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(pageSize.toString());
      return;
    }
    setPageSize(Number(value));
    setPage(0);
    setCustomRowsActive(false);
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setPageSize(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">Shipments</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/admin/shipments/carriers')}>Manage Carriers</Button>
          <Button variant="contained" onClick={() => navigate('/admin/shipments/create')}>Create Shipment</Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search shipment/container"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <TextField
              label="Shipment Number"
              size="small"
              value={shipmentNumberFilter}
              onChange={(e) => setShipmentNumberFilter(e.target.value)}
            />
            <Button variant="outlined" onClick={load}>Search</Button>
          </Stack>
          {loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ width: '100%', overflowX: 'auto', minHeight: 520 }}>
              <DataGrid
                autoHeight
                rows={rowsWithSerial}
                columns={columns}
                loading={loading}
                hideFooterPagination
                hideFooter
                disableSelectionOnClick
                sx={{
                  border: 'none',
                  minWidth: 900,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: (theme) => theme.palette.primary.main,
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1rem'
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: '1rem'
                  },
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <CustomPagination
          page={page}
          totalPages={totalPages}
          rowsPerPage={pageSize}
          onPageChange={setPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          customRowsActive={customRowsActive}
          customRowsInput={customRowsInput}
          onCustomRowsInputChange={handleCustomRowsInputChange}
          onCustomRowsInputBlur={handleCustomRowsInputBlur}
        />
      </Box>
    </Box>
  );
}
